<template>
  <div class="staff-report-tab">
    <!-- Filters Section -->
    <b-row>
      <b-col cols="12">
        <b-card class="filter-card compact">
          <b-card-body>
            <b-row class="align-items-end">
              <b-col xl="3" lg="3" md="4" sm="6" cols="12" class="mb-1">
                <b-form-group label="Staff Name" label-size="sm">
                  <v-select
                    v-model="staffNameFilter"
                    :options="uniqueStaff"
                    :placeholder="
                      uniqueStaff.length > 0
                        ? 'All staff...'
                        : 'No staff available'
                    "
                    :clearable="true"
                    :filterable="true"
                    :disabled="uniqueStaff.length === 0"
                    @input="onFilterChange"
                    class="form-select-sm"
                  />
                </b-form-group>
              </b-col>

              <b-col
                xl="3"
                lg="3"
                md="6"
                sm="6"
                cols="12"
                class="mb-1"
                :class="{ 'd-none': !hasActiveFilters }"
              >
                <b-form-group>
                  <b-button
                    @click="clearAllFilters"
                    variant="outline-danger"
                    size="sm"
                    v-b-tooltip.hover
                    title="Clear all filters"
                    class="d-flex align-items-end justify-content-end"
                  >
                    <feather-icon icon="XIcon" size="12" class="mr-1" />
                    Clear All Filters
                  </b-button>
                </b-form-group>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Results Summary -->
    <b-row class="mb-0" v-if="resultData.data.length > 0">
      <b-col cols="12">
        <small class="text-muted">
          Showing {{ filteredResults.length }} staff member(s)
        </small>
      </b-col>
    </b-row>

    <!-- Table Section -->
    <b-row class="mt-1">
      <b-col cols="12" v-if="resultData.data.length > 0">
        <b-overlay :show="tableShow" rounded="true" :fixed="true" no-fade>
          <div
            class="compact-table-container animate__animated animate__fadeIn"
          >
            <!-- No Results Message -->
            <div v-if="filteredResults.length === 0" class="text-center py-5">
              <feather-icon
                icon="SearchIcon"
                size="48"
                class="text-muted mb-3"
              />
              <h5 class="text-muted">No Results Found</h5>
              <p class="text-muted">
                Try adjusting your filters or check if data is available.
              </p>
            </div>

            <!-- Staff Results Table -->
            <div v-else class="results-table-wrapper">
              <div
                class="table-responsive horizontal-scroll"
                style="overflow-x: auto; width: 100%;"
              >
                <b-table
                  :items="paginatedResults"
                  :fields="tableFields"
                  striped
                  hover
                  small
                  class="mb-0 compact-results-table"
                  show-empty
                  empty-text="No staff review results found"
                >
                  <!-- Test Count Badge -->
                  <template #cell(test_count)="data">
                    <div class="score-badges">
                      <b-badge
                        variant="light-primary"
                        class="status-badge text-center"
                      >
                        {{ data.item.test_count || 0 }}
                      </b-badge>
                    </div>
                  </template>

                  <!-- Average Per Test -->
                  <template #cell(avg_per_test)="data">
                    <span class="text-center font-weight-bold">
                      {{ data.item.avg_per_test }}
                    </span>
                  </template>

                  <!-- Generic cell renderer for section columns -->
                  <template #cell()="data">
                    <div
                      v-if="data.field.key.startsWith('section_')"
                      class="score-badges"
                    >
                      <b-badge
                        :variant="getScoreVariant(data.value)"
                        class="status-badge section-score-badge"
                      >
                        {{ data.value }}
                      </b-badge>
                    </div>
                    <span v-else>{{ data.value }}</span>
                  </template>
                </b-table>
              </div>

              <!-- Pagination -->
              <div
                class="d-flex justify-content-between align-items-center mt-3"
              >
                <div>
                  <small class="text-muted">
                    Showing {{ startIndex + 1 }}-{{ endIndex }} of
                    {{ filteredResults.length }} items
                  </small>
                </div>
                <b-pagination
                  v-model="currentPage"
                  :total-rows="filteredResults.length"
                  :per-page="perPage"
                  :hide-goto-end-buttons="false"
                  :prev-text="'Previous'"
                  :next-text="'Next'"
                  :first-text="'First'"
                  :last-text="'Last'"
                  size="sm"
                  class="mb-0"
                />
                <div>
                  <b-form-group class="mb-0 d-flex align-items-center">
                    <label class="mr-2 mb-0 small">Items per page:</label>
                    <b-form-select
                      v-model="perPage"
                      :options="[10, 25, 50, 100]"
                      size="sm"
                      style="width: auto;"
                      @change="currentPage = 1"
                    />
                  </b-form-group>
                </div>
              </div>
            </div>
          </div>
        </b-overlay>
      </b-col>

      <b-col cols="12" v-else>
        <div class="text-center py-5">
          <feather-icon
            icon="AlertCircleIcon"
            size="64"
            class="text-muted mb-3"
          />
          <h3 class="text-muted">No Results Available</h3>
          <p class="text-muted">
            There are no results for this record. Please try a different
            selection.
          </p>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import vSelect from "vue-select";

export default {
  name: "StaffReportTab",
  components: {
    vSelect,
  },
  props: {
    resultData: {
      type: Object,
      default: () => ({ data: [], total_surveys: 0 }),
    },
    filterOptions: {
      type: Object,
      default: () => ({}),
    },
    tableShow: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // Search filters
      staffNameFilter: "",

      // Pagination
      currentPage: 1,
      perPage: 25,

      // Base table fields (will be extended with dynamic sections)
      baseTableFields: [
        {
          key: "staff_name",
          label: "Staff Name",
          sortable: true,
          thStyle: {
            width: "200px",
            fontSize: "11px",
            padding: "4px 6px",
            minWidth: "200px",
          },
          thClass: "text-center excel-header",
        },
        {
          key: "test_count",
          label: "# test",
          class: "text-center",
          thClass: "text-center excel-header",
          sortable: true,
          thStyle: {
            width: "80px",
            fontSize: "11px",
            padding: "4px 6px",
            minWidth: "80px",
          },
        },
        {
          key: "avg_per_test",
          label: "Avg Per Test",
          class: "text-center",
          thClass: "text-center excel-header",
          sortable: true,
          thStyle: {
            width: "120px",
            fontSize: "11px",
            padding: "4px 6px",
            minWidth: "120px",
          },
        },
      ],
    };
  },
  computed: {
    // Get unique sections from data
    uniqueSections() {
      const sections = new Set();
      if (this.resultData.data && this.resultData.data.length > 0) {
        this.resultData.data.forEach((group) => {
          if (group.group_name) {
            sections.add(group.group_name);
          }
        });
      }
      return Array.from(sections).sort();
    },

    // Dynamic table fields with sections as columns
    tableFields() {
      const fields = [...this.baseTableFields];

      // Add dynamic section columns
      this.uniqueSections.forEach((sectionName) => {
        fields.push({
          key: `section_${sectionName.replace(/\s+/g, "_")}`,
          label: sectionName,
          class: "text-center",
          thClass: "text-center excel-header section-header",
          sortable: true,
          thStyle: {
            width: "100px",
            fontSize: "11px",
            padding: "4px 6px",
            minWidth: "100px",
          },
        });
      });

      return fields;
    },

    staffTableData() {
      const staffData = {};

      if (!this.resultData.data || this.resultData.data.length === 0) {
        return [];
      }

      // Track unique test IDs per staff to count actual tests, not sections
      const staffTestIds = {};

      // Process each group to extract staff-level data
      this.resultData.data.forEach((group) => {
        const staffName = group.reviewee_name;
        // Use a more specific testId that includes reviewer and date for uniqueness
        const testId =
          group.id ||
          group.survey_id ||
          `${group.reviewer_name}_${group.reviewee_name}_${group.event_name}_${group.completion_time}`;

        if (!staffName) return;

        if (!staffData[staffName]) {
          staffData[staffName] = {
            staff_name: staffName,
            test_count: 0,
            total_score: 0,
            section_scores: {},
            section_counts: {},
          };
          staffTestIds[staffName] = new Set();
        }

        // Count unique tests only (not sections)
        if (!staffTestIds[staffName].has(testId)) {
          staffTestIds[staffName].add(testId);
          staffData[staffName].test_count++;
        }

        // Add section score
        const sectionName = group.group_name;
        const sectionScore = parseFloat(group.questions_scores_summed) || 0;

        if (!staffData[staffName].section_scores[sectionName]) {
          staffData[staffName].section_scores[sectionName] = 0;
          staffData[staffName].section_counts[sectionName] = 0;
        }

        staffData[staffName].section_scores[sectionName] += sectionScore;
        staffData[staffName].section_counts[sectionName]++;
        staffData[staffName].total_score += sectionScore;
      });

      // Convert to array and calculate averages
      return Object.values(staffData).map((staff) => {
        // Calculate average per test
        const avgPerTest =
          staff.test_count > 0
            ? (staff.total_score / staff.test_count).toFixed(2)
            : "0.00";

        // Build the row object with base fields
        const rowData = {
          id: `staff-${staff.staff_name}`,
          staff_name: staff.staff_name,
          test_count: staff.test_count,
          avg_per_test: avgPerTest,
        };

        // Add dynamic section columns
        this.uniqueSections.forEach((sectionName) => {
          const columnKey = `section_${sectionName.replace(/\s+/g, "_")}`;
          const totalScore = staff.section_scores[sectionName] || 0;
          const count = staff.section_counts[sectionName] || 0;
          rowData[columnKey] =
            count > 0 ? (totalScore / count).toFixed(2) : "0.00";
        });

        return rowData;
      });
    },

    filteredResults() {
      let filtered = this.staffTableData;

      if (this.staffNameFilter) {
        filtered = filtered.filter(
          (item) =>
            item.staff_name &&
            item.staff_name
              .toLowerCase()
              .includes(this.staffNameFilter.toLowerCase())
        );
      }

      return filtered;
    },

    paginatedResults() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredResults.slice(start, end);
    },

    startIndex() {
      return (this.currentPage - 1) * this.perPage;
    },

    endIndex() {
      const end = this.startIndex + this.perPage;
      return end > this.filteredResults.length
        ? this.filteredResults.length
        : end;
    },

    hasActiveFilters() {
      return !!this.staffNameFilter;
    },

    uniqueStaff() {
      // Use API-provided filter options for consistency across all tabs
      return this.filterOptions.reviewees || [];
    },
  },

  methods: {
    onFilterChange() {
      this.currentPage = 1;
      this.$emit("filter-change", {
        staffName: this.staffNameFilter,
      });
    },

    clearAllFilters() {
      this.staffNameFilter = "";
      this.currentPage = 1;
      this.$emit("filter-change", {});
    },

    getScoreVariant(score) {
      const numScore = parseFloat(score);
      if (numScore >= 80) return "success";
      if (numScore >= 60) return "warning";
      return "danger";
    },

    formatDate(dateString) {
      if (!dateString) return "";
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString("en-GB");
      } catch (error) {
        return dateString;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// Import the same styles from the original file
@import "@core/scss/vue/libs/vue-select.scss";

.staff-report-tab {
  // Use the same styling as the original component
  .filter-card {
    border: 1px solid #e3e6f0;
    box-shadow: 0 0.15rem 1.75rem 0 rgba(58, 59, 69, 0.15);
    border-radius: 0.35rem;

    &.compact {
      .card-body {
        padding: 0.5rem 1rem;
      }
    }

    .form-group {
      margin-bottom: 0.25rem;

      label {
        font-weight: 500;
        color: #5a5c69;
        font-size: 0.75rem;
        margin-bottom: 0.25rem;
      }
    }

    .v-select.form-select-sm {
      .vs__dropdown-toggle {
        border: 1px solid #d1d3e2;
        border-radius: 0.25rem;
        min-height: 32px;

        &:hover {
          border-color: #a0a0a0;
        }

        &.vs--open {
          border-color: #80bdff;
          box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        }
      }

      .vs__selected {
        font-size: 0.875rem;
        color: #495057;
      }

      .vs__search {
        font-size: 0.875rem;
      }

      .vs__dropdown-menu {
        font-size: 0.875rem;
        z-index: 9999;
      }
    }
  }

  // Excel-style table headers
  .excel-header {
    background-color: #f8f9fa !important;
    border: 1px solid #dee2e6 !important;
    font-weight: 600 !important;
    font-size: 11px !important;
    padding: 4px 6px !important;
    text-align: center !important;
    color: #495057 !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
    vertical-align: middle !important;

    // Special styling for section headers
    &.section-header {
      background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%) !important;
      color: #1565c0 !important;
      font-weight: 700 !important;
      border-color: #2196f3 !important;
    }
  }

  // Section score badges
  .section-score-badge {
    font-weight: 600;
    font-size: 0.8rem;
    padding: 0.35rem 0.6rem;
    border-radius: 0.25rem;
    min-width: 50px;
    display: inline-block;
    text-align: center;
  }

  .score-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    align-items: center;
    justify-content: center;
  }

  .compact-table-container {
    width: 100%;
    overflow-x: auto;

    .results-table-wrapper {
      background: white;
      border-radius: 0.375rem;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      overflow: hidden;
      width: 100%;

      .table-responsive {
        overflow-x: auto !important;
        -webkit-overflow-scrolling: touch;

        &::-webkit-scrollbar {
          height: 8px;
        }

        &::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }

        &::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 4px;

          &:hover {
            background: #a1a1a1;
          }
        }
      }

      .horizontal-scroll {
        overflow-x: auto !important;
        overflow-y: visible !important;
        white-space: nowrap !important;

        .table {
          min-width: max-content !important;
          white-space: nowrap !important;

          th,
          td {
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
          }
        }
      }
    }

    .table {
      margin-bottom: 0;
      font-size: 0.875rem;

      th,
      td {
        vertical-align: middle;
        border-color: #e3e6f0;
        padding: 0.5rem 0.75rem;
      }

      th {
        background-color: #f8f9fa;
        font-weight: 600;
        border-bottom: 2px solid #e3e6f0;
      }
    }

    .card-body {
      padding: 1rem 0.75rem;
    }
  }

  .compact-results-table {
    font-size: 0.875rem;
    border-collapse: collapse;
    border: 1px solid #dee2e6;
    table-layout: auto; // Allow dynamic column widths
    width: 100%;
    min-width: max-content;

    thead th {
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      border-bottom: 2px solid #dee2e6;
      border-right: 1px solid #dee2e6;
      font-weight: 600;
      font-size: 0.8rem;
      padding: 0.75rem 0.5rem;
      vertical-align: middle;
      min-width: 100px !important;
      max-width: 400px !important;
      width: auto !important;
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
      white-space: normal !important;
      word-break: normal !important;
    }

    tbody td {
      padding: 0.6rem 0.5rem;
      vertical-align: middle;
      border-top: 1px solid #dee2e6;
      border-right: 1px solid #dee2e6;
      border-bottom: 1px solid #f8f9fa;
      min-width: 100px !important;
      max-width: 400px !important;
      width: auto !important;
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
      white-space: normal !important;
      word-break: normal !important;
    }

    tbody tr:hover {
      background-color: rgba(0, 123, 255, 0.04);
    }

    tbody tr:nth-child(even) {
      background-color: #f8f9fa;
    }
  }
}

// Disabled filter styling
.v-select.vs--disabled {
  opacity: 0.6;

  .vs__dropdown-toggle {
    background-color: #f8f9fa !important;
    cursor: not-allowed;
  }

  .vs__selected-options {
    color: #6c757d !important;
  }

  .vs__search {
    cursor: not-allowed;
  }
}
</style>
